import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../lib/utils";

function VerticalMarquee({
  children,
  pauseOnHover = false,
  reverse = false,
  className,
  speed = 30,
  onItemsRef,
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (onItemsRef && containerRef.current) {
      const items = Array.from(
        containerRef.current.querySelectorAll(".marquee-item"),
      );
      onItemsRef(items);
    }
  }, [onItemsRef]);

  return (
    <div
      ref={containerRef}
      className={cn("group flex flex-col overflow-hidden", className)}
      style={{ "--duration": `${speed}s` }}
    >
      <div
        className={cn(
          "flex shrink-0 flex-col animate-marquee-vertical",
          reverse && "[animation-direction:reverse]",
          pauseOnHover && "group-hover:[animation-play-state:paused]",
        )}
      >
        {children}
      </div>
      <div
        className={cn(
          "flex shrink-0 flex-col animate-marquee-vertical",
          reverse && "[animation-direction:reverse]",
          pauseOnHover && "group-hover:[animation-play-state:paused]",
        )}
        aria-hidden="true"
      >
        {children}
      </div>
    </div>
  );
}

const defaultItems = [
  "Founders & Startups",
  "Restaurants & Cafés",
  "Construction & Trades",
  "Photographers & Creatives",
  "Local Service Pros",
  "E-commerce Brands",
];

export default function CTAWithVerticalMarquee({
  title = "Get Started in Minutes",
  description = "Start getting more distribution and ROI out of your content. Try Assembly for free for 14 days.",
  primaryButtonText = "START PROJECT",
  primaryHref = "/start-project",
  secondaryButtonText = "BOOK A FREE CALL",
  secondaryHref = "/start-project",
  items = defaultItems,
}) {
  const navigate = useNavigate();
  const marqueeRef = useRef(null);

  useEffect(() => {
    const marqueeContainer = marqueeRef.current;
    if (!marqueeContainer) return;

    let frameId;

    const updateOpacity = () => {
      const itemNodes =
        marqueeContainer.querySelectorAll(".marquee-item");
      const containerRect = marqueeContainer.getBoundingClientRect();
      const centerY = containerRect.top + containerRect.height / 2;

      itemNodes.forEach((item) => {
        const itemRect = item.getBoundingClientRect();
        const itemCenterY = itemRect.top + itemRect.height / 2;
        const distance = Math.abs(centerY - itemCenterY);
        const maxDistance = containerRect.height / 2;
        const normalizedDistance = Math.min(distance / maxDistance, 1);
        const opacity = 1 - normalizedDistance * 0.75;
        item.style.opacity = opacity.toString();
      });
    };

    const loop = () => {
      updateOpacity();
      frameId = requestAnimationFrame(loop);
    };

    frameId = requestAnimationFrame(loop);

    return () => cancelAnimationFrame(frameId);
  }, []);

  const goTo = (href) => {
    if (typeof href === "string" && href.startsWith("/")) {
      navigate(href);
    } else if (typeof href === "string") {
      window.location.href = href;
    }
  };

  return (
    <div className="relative w-full bg-background text-foreground flex items-center justify-center px-6 py-20 lg:py-28 overflow-hidden">
      <div className="w-full max-w-7xl animate-fade-in-up">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          <div className="space-y-8 max-w-xl">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium leading-tight tracking-tight text-foreground animate-fade-in-up [animation-delay:200ms]">
              {title}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-in-up [animation-delay:400ms]">
              {description}
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up [animation-delay:600ms]">
              <button
                onClick={() => goTo(primaryHref)}
                className="group relative px-6 py-3 bg-foreground text-background rounded-md font-medium overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                <span className="relative z-10">{primaryButtonText}</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
              </button>
              <button
                onClick={() => goTo(secondaryHref)}
                className="group relative px-6 py-3 bg-secondary text-secondary-foreground rounded-md font-medium overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-lg border border-border"
              >
                <span className="relative z-10">{secondaryButtonText}</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-foreground/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
              </button>
            </div>
          </div>

          <div
            ref={marqueeRef}
            className="relative h-[480px] lg:h-[600px] flex items-center justify-center animate-fade-in-up [animation-delay:400ms]"
          >
            <div className="relative w-full h-full">
              <VerticalMarquee speed={22} className="h-full">
                {items.map((item, idx) => (
                  <div
                    key={idx}
                    className="text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-light tracking-tight py-8 marquee-item text-center lg:text-left"
                  >
                    {item}
                  </div>
                ))}
              </VerticalMarquee>

              <div className="pointer-events-none absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-background via-background/50 to-transparent z-10" />
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-background via-background/50 to-transparent z-10" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
